import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { Spinner } from '@/components/ui/Spinner';
import {
  ForwardCycleTaskModal,
  type ForwardCycleTaskRow,
} from '@/components/ForwardCycleTaskModal';
import { cn } from '@/lib/cn';

type MiniprogramOption = { id: number; name: string };

type CompletedRobotChip = { id: number; name: string };

type TaskRow = {
  id: number;
  site_id: number;
  site_name?: string;
  entry_id: number;
  type_key?: string;
  type_label?: string;
  title_text: string;
  body_text: string;
  serial_number: string;
  created_at: string;
  miniprogram_hash_link?: string;
  completed_groups_robots?: CompletedRobotChip[];
  completed_moments_robots?: CompletedRobotChip[];
  completed_robots?: CompletedRobotChip[];
};

type PickerEntry = {
  id: number;
  site_id: number;
  site_name?: string;
  serial_number: string;
  title_text: string;
  body_text: string;
  type_label?: string;
};

const PER_PAGE = 12;

const selectClass =
  'w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3.5 text-lg font-semibold text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/20';

function forwardErr(err?: string, detail?: string): string {
  if (err === 'robot_module_disabled') {
    return detail || '本站点在本小程序下未开启转发模块，请联系管理员。';
  }
  return detail?.trim() || err || '操作失败';
}

function channelLabel(channel: string): string {
  if (channel === 'moments') return '仅发圈';
  if (channel === 'groups') return '仅群发';
  return '发圈+群发';
}

function statusLabel(status: string): string {
  if (status === 'paused') return '已暂停';
  if (status === 'completed') return '已完成';
  return '进行中';
}

export function SiteMasterForwardPage() {
  const { sites, currentSiteId } = useSiteMasterSession();
  const [miniprograms, setMiniprograms] = useState<MiniprogramOption[]>([]);
  const [mpLoading, setMpLoading] = useState(true);
  const [mpId, setMpId] = useState(0);

  const [tab, setTab] = useState<'queue' | 'cycle'>('queue');
  const [items, setItems] = useState<TaskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [picker, setPicker] = useState<PickerEntry[]>([]);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerQ, setPickerQ] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);

  const [cycleItems, setCycleItems] = useState<ForwardCycleTaskRow[]>([]);
  const [cycleTotal, setCycleTotal] = useState(0);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [cycleStatus, setCycleStatus] = useState<'all' | 'active' | 'paused' | 'completed'>('all');
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleEdit, setCycleEdit] = useState<ForwardCycleTaskRow | null>(null);
  const [cycleSiteId, setCycleSiteId] = useState(0);

  const actionSiteId = useMemo(() => {
    if (currentSiteId > 0) return currentSiteId;
    if (sites.length === 1) return sites[0].id;
    return 0;
  }, [currentSiteId, sites]);

  useEffect(() => {
    let cancelled = false;
    setMpLoading(true);
    void (async () => {
      try {
        const r = await apiFetch<{ ok: boolean; items: MiniprogramOption[] }>('site_master_list_miniprograms', {
          method: 'GET',
        });
        if (!cancelled && r.ok) {
          setMiniprograms(r.items ?? []);
          if ((r.items?.length ?? 0) > 0) {
            setMpId((prev) => (prev > 0 ? prev : r.items![0].id));
          }
        }
      } catch {
        if (!cancelled) setErr('无法加载小程序列表');
      } finally {
        if (!cancelled) setMpLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadQueue = useCallback(async () => {
    if (mpId < 1) return;
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        items: TaskRow[];
        total: number;
        detail?: string;
      }>('site_master_list_forward_tasks', {
        method: 'GET',
        query: {
          miniprogram_id: mpId,
          page,
          per_page: PER_PAGE,
          ...(appliedQ ? { q: appliedQ } : {}),
        },
      });
      if (r.ok) {
        setItems(r.items ?? []);
        setTotal(r.total ?? 0);
      } else {
        setErr('加载队列失败');
      }
    } catch (e) {
      if (e instanceof Error && e.message !== 'SESSION_EXPIRED') {
        setErr('加载队列失败');
      }
    } finally {
      setLoading(false);
    }
  }, [mpId, page, appliedQ, currentSiteId]);

  const loadCycle = useCallback(async () => {
    if (mpId < 1) return;
    setCycleLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: ForwardCycleTaskRow[]; total: number }>(
        'site_master_list_forward_cycle_tasks',
        {
          method: 'GET',
          query: {
            miniprogram_id: mpId,
            page: 1,
            per_page: 50,
            ...(cycleStatus !== 'all' ? { status: cycleStatus } : {}),
          },
        },
      );
      if (r.ok) {
        setCycleItems(r.items ?? []);
        setCycleTotal(r.total ?? 0);
      }
    } catch {
      /* ignore */
    } finally {
      setCycleLoading(false);
    }
  }, [mpId, cycleStatus, currentSiteId]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    void loadCycle();
  }, [loadCycle]);

  useEffect(() => {
    setPage(1);
  }, [mpId, currentSiteId, appliedQ]);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(''), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const loadPicker = useCallback(
    async (p = 1, q = '') => {
      if (mpId < 1) return;
      setPickerLoading(true);
      try {
        const r = await apiFetch<{ ok: boolean; items: PickerEntry[]; total: number }>(
          'site_master_list_entries_for_forward',
          {
            method: 'GET',
            query: {
              miniprogram_id: mpId,
              page: p,
              per_page: 12,
              ...(q.trim() ? { q: q.trim() } : {}),
            },
          },
        );
        if (r.ok) {
          setPicker(r.items ?? []);
          setPickerTotal(r.total ?? 0);
          setPickerPage(p);
        }
      } finally {
        setPickerLoading(false);
      }
    },
    [mpId, currentSiteId],
  );

  function openAdd() {
    setAddOpen(true);
    setPickerQ('');
    void loadPicker(1, '');
  }

  async function addTask(entry: PickerEntry) {
    const key = `add:${entry.id}`;
    setBusyKey(key);
    setToast('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('site_master_add_forward_task', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: entry.site_id,
          entry_id: entry.id,
        }),
      });
      if (!r.ok) {
        setToast(forwardErr(r.err, r.detail));
        return;
      }
      setToast('已加入转发队列');
      setAddOpen(false);
      await loadQueue();
    } finally {
      setBusyKey(null);
    }
  }

  async function removeTask(row: TaskRow) {
    if (!window.confirm(`确定将「${row.title_text || `#${row.entry_id}`}」移出转发队列？`)) return;
    const key = `del:${row.id}`;
    setBusyKey(key);
    try {
      const r = await apiFetch<{ ok: boolean; detail?: string; err?: string }>('site_master_delete_forward_task', {
        method: 'POST',
        body: JSON.stringify({
          miniprogram_id: mpId,
          site_id: row.site_id,
          task_id: row.id,
        }),
      });
      if (!r.ok) {
        setToast(forwardErr(r.err, r.detail));
        return;
      }
      setToast('已移出队列');
      await loadQueue();
    } finally {
      setBusyKey(null);
    }
  }

  async function removeCompleted(
    row: TaskRow,
    robot: CompletedRobotChip,
    kind: 'groups' | 'moments',
  ) {
    const kindLabel = kind === 'moments' ? '发圈' : '群发';
    if (!window.confirm(`清除「${robot.name}」的${kindLabel}完成记录？该机器人可再次拉取。`)) return;
    const key = `rm:${kind}:${row.id}:${robot.id}`;
    setBusyKey(key);
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(
        'site_master_remove_forward_task_completed_robot',
        {
          method: 'POST',
          body: JSON.stringify({
            miniprogram_id: mpId,
            site_id: row.site_id,
            task_id: row.id,
            robot_id: robot.id,
            completion_kind: kind,
          }),
        },
      );
      if (!r.ok) {
        setToast(forwardErr(r.err, r.detail));
        return;
      }
      setToast(`已清除${kindLabel}完成记录`);
      await loadQueue();
    } finally {
      setBusyKey(null);
    }
  }

  async function regenHash(row: TaskRow) {
    const key = `hash:${row.id}`;
    setBusyKey(key);
    try {
      const r = await apiFetch<{ ok: boolean; hash_link?: string; err?: string; detail?: string }>(
        'site_master_regenerate_forward_task_hash_link',
        {
          method: 'POST',
          body: JSON.stringify({
            miniprogram_id: mpId,
            site_id: row.site_id,
            entry_id: row.entry_id,
            task_id: row.id,
          }),
        },
      );
      if (!r.ok) {
        setToast(forwardErr(r.err, r.detail));
        return;
      }
      setToast('短链已生成');
      await loadQueue();
    } finally {
      setBusyKey(null);
    }
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setToast('已复制');
    } catch {
      setToast('复制失败，请长按手动复制');
    }
  }

  function openCycleCreate() {
    if (currentSiteId === 0 && sites.length > 1) {
      setToast('请先在顶部切换到具体站点后再添加循环任务');
      return;
    }
    const sid = actionSiteId > 0 ? actionSiteId : sites[0]?.id ?? 0;
    if (sid < 1) {
      setToast('暂无可用站点');
      return;
    }
    setCycleSiteId(sid);
    setCycleEdit(null);
    setCycleOpen(true);
  }

  function openCycleEdit(row: ForwardCycleTaskRow) {
    setCycleSiteId(row.site_id);
    setCycleEdit(row);
    setCycleOpen(true);
  }

  async function toggleCycleStatus(row: ForwardCycleTaskRow) {
    const next = row.status === 'paused' ? 'active' : 'paused';
    const r = await apiFetch<{ ok: boolean; detail?: string; err?: string }>(
      'site_master_update_forward_cycle_task',
      {
        method: 'POST',
        body: JSON.stringify({
          id: row.id,
          site_id: row.site_id,
          miniprogram_id: mpId,
          status: next,
        }),
      },
    );
    if (!r.ok) {
      setToast(forwardErr(r.err, r.detail));
      return;
    }
    setToast(next === 'paused' ? '已暂停' : '已恢复');
    await loadCycle();
  }

  async function deleteCycle(row: ForwardCycleTaskRow) {
    if (!window.confirm(`确定删除循环任务「${row.title || `#${row.id}`}」？`)) return;
    const r = await apiFetch<{ ok: boolean; detail?: string; err?: string }>(
      'site_master_delete_forward_cycle_task',
      {
        method: 'POST',
        body: JSON.stringify({
          id: row.id,
          site_id: row.site_id,
          miniprogram_id: mpId,
        }),
      },
    );
    if (!r.ok) {
      setToast(forwardErr(r.err, r.detail));
      return;
    }
    setToast('已删除循环任务');
    await loadCycle();
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pickerPages = Math.max(1, Math.ceil(pickerTotal / 12));

  if (mpLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="加载中" />
      </div>
    );
  }

  if (miniprograms.length === 0) {
    return (
      <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6 text-lg text-amber-950">
        当前站点尚未关联可管理的小程序，无法使用转发管理。请联系管理员配置。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80">
        <h1 className="text-2xl font-bold text-slate-900">转发管理</h1>
        <p className="mt-2 text-base text-slate-600">管理已开通小程序的转发任务队列与循环推广。</p>
        <label className="mt-4 block">
          <span className="mb-2 block text-base font-semibold text-slate-700">选择小程序</span>
          <select className={selectClass} value={mpId} onChange={(e) => setMpId(Number(e.target.value) || 0)}>
            {miniprograms.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      {toast ? (
        <p className="rounded-2xl border-2 border-teal-200 bg-teal-50 px-4 py-3 text-base font-semibold text-teal-900">
          {toast}
        </p>
      ) : null}
      {err ? (
        <p className="rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-900">{err}</p>
      ) : null}

      <div className="flex gap-2 rounded-2xl bg-slate-100 p-1.5">
        <button
          type="button"
          onClick={() => setTab('queue')}
          className={cn(
            'flex-1 rounded-xl py-3 text-base font-bold',
            tab === 'queue' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600',
          )}
        >
          一次性队列
        </button>
        <button
          type="button"
          onClick={() => setTab('cycle')}
          className={cn(
            'flex-1 rounded-xl py-3 text-base font-bold',
            tab === 'cycle' ? 'bg-white text-sky-800 shadow-sm' : 'text-slate-600',
          )}
        >
          循环任务
        </button>
      </div>

      {tab === 'queue' ? (
        <>
          <div className="flex flex-wrap gap-2">
            <input
              className="min-w-0 flex-1 rounded-2xl border-2 border-slate-200 px-4 py-3 text-base"
              placeholder="搜索标题、编号…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedQ(qInput.trim());
                  setPage(1);
                }
              }}
            />
            <button
              type="button"
              className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-base font-semibold text-slate-700"
              onClick={() => {
                setAppliedQ(qInput.trim());
                setPage(1);
              }}
            >
              搜索
            </button>
            <button
              type="button"
              className="rounded-2xl bg-sky-600 px-4 py-3 text-base font-bold text-white"
              onClick={openAdd}
            >
              添加任务
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner label="加载队列" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-8 text-center text-base text-slate-500">
              暂无转发任务。可点击「添加任务」从信息列表入队。
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((row) => {
                const groups = row.completed_groups_robots ?? row.completed_robots ?? [];
                const moments = row.completed_moments_robots ?? [];
                return (
                  <li key={row.id} className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-lg font-bold text-slate-900">
                          {row.title_text || `信息 #${row.entry_id}`}
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                          #{row.entry_id}
                          {row.serial_number ? ` · ${row.serial_number}` : ''}
                          {row.site_name ? ` · ${row.site_name}` : ''}
                          {row.type_label ? ` · ${row.type_label}` : ''}
                        </p>
                        <p className="mt-1 text-sm text-slate-400">入队 {row.created_at}</p>
                      </div>
                      <button
                        type="button"
                        disabled={busyKey === `del:${row.id}`}
                        className="shrink-0 rounded-xl border-2 border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700"
                        onClick={() => void removeTask(row)}
                      >
                        移出
                      </button>
                    </div>

                    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                      <p className="text-sm font-semibold text-slate-700">小程序短链</p>
                      {row.miniprogram_hash_link ? (
                        <div className="flex flex-wrap gap-2">
                          <p className="min-w-0 flex-1 break-all rounded-xl bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">
                            {row.miniprogram_hash_link}
                          </p>
                          <button
                            type="button"
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-sky-700"
                            onClick={() => void copyText(row.miniprogram_hash_link || '')}
                          >
                            复制
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={busyKey === `hash:${row.id}`}
                          className="rounded-xl border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
                          onClick={() => void regenHash(row)}
                        >
                          {busyKey === `hash:${row.id}` ? '生成中…' : '补生成短链'}
                        </button>
                      )}
                    </div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="mb-1.5 text-sm font-semibold text-slate-700">已完成群发</p>
                        {groups.length === 0 ? (
                          <p className="text-sm text-slate-400">—</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {groups.map((rb) => (
                              <li
                                key={`g-${rb.id}`}
                                className="flex items-center gap-2 rounded-xl bg-slate-50 px-2.5 py-1.5 text-sm"
                              >
                                <span className="min-w-0 flex-1 truncate">{rb.name}</span>
                                <button
                                  type="button"
                                  className="text-rose-500"
                                  disabled={busyKey === `rm:groups:${row.id}:${rb.id}`}
                                  onClick={() => void removeCompleted(row, rb, 'groups')}
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <p className="mb-1.5 text-sm font-semibold text-slate-700">已完成发圈</p>
                        {moments.length === 0 ? (
                          <p className="text-sm text-slate-400">—</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {moments.map((rb) => (
                              <li
                                key={`m-${rb.id}`}
                                className="flex items-center gap-2 rounded-xl bg-slate-50 px-2.5 py-1.5 text-sm"
                              >
                                <span className="min-w-0 flex-1 truncate">{rb.name}</span>
                                <button
                                  type="button"
                                  className="text-rose-500"
                                  disabled={busyKey === `rm:moments:${row.id}:${rb.id}`}
                                  onClick={() => void removeCompleted(row, rb, 'moments')}
                                >
                                  ×
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-1">
              <button
                type="button"
                disabled={page <= 1}
                className="rounded-xl border-2 border-slate-200 px-4 py-2 text-base font-semibold disabled:opacity-40"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                上一页
              </button>
              <span className="text-base text-slate-600">
                {page} / {totalPages} · 共 {total} 条
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                className="rounded-xl border-2 border-slate-200 px-4 py-2 text-base font-semibold disabled:opacity-40"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                下一页
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-1 flex-wrap gap-1.5">
              {(['all', 'active', 'paused', 'completed'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setCycleStatus(k)}
                  className={cn(
                    'rounded-xl px-3 py-2 text-sm font-semibold',
                    cycleStatus === k
                      ? 'bg-sky-600 text-white'
                      : 'border border-slate-200 bg-white text-slate-600',
                  )}
                >
                  {k === 'all' ? '全部' : statusLabel(k)}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="rounded-2xl bg-sky-600 px-4 py-3 text-base font-bold text-white"
              onClick={openCycleCreate}
            >
              添加循环
            </button>
          </div>
          {currentSiteId === 0 && sites.length > 1 ? (
            <p className="text-sm text-amber-800">当前为「全部站点」视图：可浏览各站循环任务；添加前请先切换到具体站点。</p>
          ) : null}

          {cycleLoading ? (
            <div className="flex justify-center py-10">
              <Spinner label="加载循环任务" />
            </div>
          ) : cycleItems.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-8 text-center text-base text-slate-500">
              暂无循环任务（共 {cycleTotal}）。可添加固定短链按频次推广。
            </div>
          ) : (
            <ul className="space-y-3">
              {cycleItems.map((row) => (
                <li key={row.id} className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-lg font-bold text-slate-900">{row.title || `循环 #${row.id}`}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {row.freq_label} · {channelLabel(row.channel)} · {statusLabel(row.status)}
                      </p>
                      <p className="mt-1 break-all font-mono text-xs text-slate-600">{row.hash_link}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold"
                      onClick={() => openCycleEdit(row)}
                    >
                      编辑
                    </button>
                    {row.status !== 'completed' ? (
                      <button
                        type="button"
                        className="rounded-xl border-2 border-amber-200 px-3 py-2 text-sm font-semibold text-amber-900"
                        onClick={() => void toggleCycleStatus(row)}
                      >
                        {row.status === 'paused' ? '恢复' : '暂停'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-xl border-2 border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700"
                      onClick={() => void deleteCycle(row)}
                    >
                      删除
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {addOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-xl font-bold text-slate-900">添加转发任务</h2>
              <button type="button" className="text-2xl text-slate-400" onClick={() => setAddOpen(false)}>
                ×
              </button>
            </div>
            <div className="flex gap-2 border-b border-slate-100 px-4 py-3">
              <input
                className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-base"
                placeholder="搜索信息…"
                value={pickerQ}
                onChange={(e) => setPickerQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void loadPicker(1, pickerQ);
                }}
              />
              <button
                type="button"
                className="rounded-xl bg-slate-800 px-4 py-2.5 text-base font-semibold text-white"
                onClick={() => void loadPicker(1, pickerQ)}
              >
                搜
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {pickerLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner label="加载信息" />
                </div>
              ) : picker.length === 0 ? (
                <p className="py-8 text-center text-base text-slate-500">没有可入队的信息</p>
              ) : (
                <ul className="space-y-2">
                  {picker.map((e) => (
                    <li key={e.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 p-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-base font-semibold text-slate-900">
                          {e.title_text || `信息 #${e.id}`}
                        </p>
                        <p className="truncate text-sm text-slate-500">
                          #{e.id}
                          {e.site_name ? ` · ${e.site_name}` : ''}
                          {e.type_label ? ` · ${e.type_label}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={busyKey === `add:${e.id}`}
                        className="shrink-0 rounded-xl bg-sky-600 px-3 py-2 text-sm font-bold text-white disabled:opacity-50"
                        onClick={() => void addTask(e)}
                      >
                        入队
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {pickerPages > 1 ? (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <button
                  type="button"
                  disabled={pickerPage <= 1}
                  className="text-base font-semibold disabled:opacity-40"
                  onClick={() => void loadPicker(pickerPage - 1, pickerQ)}
                >
                  上一页
                </button>
                <span className="text-sm text-slate-500">
                  {pickerPage}/{pickerPages}
                </span>
                <button
                  type="button"
                  disabled={pickerPage >= pickerPages}
                  className="text-base font-semibold disabled:opacity-40"
                  onClick={() => void loadPicker(pickerPage + 1, pickerQ)}
                >
                  下一页
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <ForwardCycleTaskModal
        open={cycleOpen}
        mpId={mpId}
        siteId={cycleSiteId}
        editItem={cycleEdit}
        onClose={() => setCycleOpen(false)}
        onSaved={() => void loadCycle()}
        createAction="site_master_create_forward_cycle_task"
        updateAction="site_master_update_forward_cycle_task"
      />
    </div>
  );
}
